import { makeObservable, observable } from "mobx";
import React from "react";
import LinkToFile from "../components/sharedComponents/linkToFile/linkToFile";
import { defaultTuzAccession } from "./client";
import Formatter from "./formatter";

export const formattedDefaultTuzAccession = Formatter.decimalPriceFormat(Formatter.convertCentsToNormalNumber(defaultTuzAccession));

export enum RequiredConsentsId {
  termsOfService = "termsOfService",
  owu = "owu",
  salary = "salary",
  accessionToTuz = "accessionToTuz",
  productCard = "productCard",
  insured = "insured",
  nnw = "nnw"
}

export enum AdditionalConsentsId {
  dataProcessingForMarketing = "dataProcessingForMarketing",
  thirdPartyMarketing = "thirdPartyMarketing",
  electronicMeansMarketing = "electronicMeansMarketing",
  phoneCallMarketing = "phoneCallMarketing"
}

export interface ConsentModel {
  title?: string;
  description: JSX.Element;
  required: boolean;
}

export interface RequiredConsents {
  termsOfService: ConsentModel;
  owu: ConsentModel;
  salary: ConsentModel;
  accessionToTuz: ConsentModel;
  productCard: ConsentModel;
  insured: ConsentModel;
  nnw: ConsentModel;
}

export interface AdditionalConsents {
  dataProcessingForMarketing: ConsentModel;
  thirdPartyMarketing: ConsentModel;
  electronicMeansMarketing: ConsentModel;
  phoneCallMarketing: ConsentModel;
}

export interface ConsentsValuesModel {
  value: boolean;
  error?: boolean;
}

export interface ConsentsModel {
  [RequiredConsentsId.termsOfService]: ConsentsValuesModel;
  [RequiredConsentsId.owu]: ConsentsValuesModel;
  [RequiredConsentsId.salary]: ConsentsValuesModel;
  [RequiredConsentsId.accessionToTuz]: ConsentsValuesModel;
  [RequiredConsentsId.productCard]: ConsentsValuesModel;
  [RequiredConsentsId.insured]: ConsentsValuesModel;
  [RequiredConsentsId.nnw]: ConsentsValuesModel;
  [AdditionalConsentsId.dataProcessingForMarketing]: ConsentsValuesModel;
  [AdditionalConsentsId.thirdPartyMarketing]: ConsentsValuesModel;
  [AdditionalConsentsId.electronicMeansMarketing]: ConsentsValuesModel;
  [AdditionalConsentsId.phoneCallMarketing]: ConsentsValuesModel;
}

export default class ConsentsValues implements ConsentsModel {
  constructor() {
    makeObservable(this);
  }

  @observable accessionToTuz = { value: false, error: false };
  @observable dataProcessingForMarketing = { value: false };
  @observable electronicMeansMarketing = { value: false };
  @observable owu = { value: false, error: false };
  @observable productCard = { value: false, error: false };
  @observable insured = { value: false, error: false };
  @observable nnw = { value: false, error: false };
  @observable phoneCallMarketing = { value: false };
  @observable salary = { value: false, error: false };
  @observable termsOfService = { value: false, error: false };
  @observable thirdPartyMarketing = { value: false };

}

export class ConsentsConfiguration {
  public static getRequiredConsents(): RequiredConsents {
    return ({
      [RequiredConsentsId.termsOfService]: {
        description: <>
          Oświadczam, że zapoznałem(-am) się i akceptuję <a
            href="https://tuz.pl/strefa-klienta/regulaminy/"
            target="_blank" rel="noopener noreferrer"
            className="link-to-file">Regulamin świadczenia usług drogą elektroniczną</a> przez TUZ
          Towarzystwo Ubezpieczeń Wzajemnych.
        </>,
        required: true
      },
      [RequiredConsentsId.owu]: {
        description: <>
          Oświadczam, że przed zawarciem umowy ubezpieczenia udostępniono mi na trwałym nośniku informacji, na co wyraziłem/am zgodę, <LinkToFile
            displayText="Ogólne Warunki Ubezpieczenia"
            fileName="OWU Bezpieczny Dom.pdf"
          /> określone w niniejszym dokumencie ubezpieczenia (polisie), stanowiące podstawę zawarcia umowy ubezpieczenia, z którymi zapoznałem/am się.
        </>,
        required: true
      },
      [RequiredConsentsId.productCard]: {
        description: <>
          Oświadczam, że przed zawarciem umowy ubezpieczenia udostępniono mi na trwałym nośniku informacji, na co wyraziłem/am zgodę, <LinkToFile
            fileName="Karta produktu.pdf"
            displayText="ustandaryzowany dokument zawierający informacje o produkcie ubezpieczeniowym" />, (jestem świadomy(-a), że dokument ten nie stanowi części umowy ubezpieczenia
          i ma charakter informacyjny), z którym zapoznałem/am się.
        </>,
        required: true
      },
      [RequiredConsentsId.salary]: {
        description: <>
          Oświadczam, że przed zawarciem umowy ubezpieczenia dystrybutor ubezpieczeń poinformował mnie o
          charakterze wynagrodzenia otrzymywanego w przypadku zawarcia umowy.
        </>,
        required: true
      },
      [RequiredConsentsId.insured]: {
        description: <>
          Oświadczam, że w razie zawarcia umowy ubezpieczenia na cudzy rachunek - zobowiązuję się poinformować
          Ubezpieczonego o zawartej umowie na jego rachunek oraz zobowiązuję się udostępnić Ubezpieczonemu,
          w sposób z nim ustalony, informacje o zawartej umowie ubezpieczenia, treści <LinkToFile
            displayText="OWU"
            fileName="OWU Bezpieczny Dom.pdf"
          /> oraz trybie postępowania w razie zajścia wypadku ubezpieczeniowego.
        </>,
        required: true
      },
      [RequiredConsentsId.accessionToTuz]: {
        description: <>
          Zawierając umowę ubezpieczenia wyrażam zgodę na przystąpienie do TUZ TUW poprzez:
          <ul>
            <li> wpłacenie wpisowego w wysokości {formattedDefaultTuzAccession} zł, które z chwilą uzyskania członkostwa jest
              bezzwrotne,
            </li>
            <li> nabycie i opłacenie 1 (jednego) udziału w funduszu udziałowym o wartości nominalnej 10,00
              zł.
            </li>
          </ul>
        </>,
        required: true
      },
      [RequiredConsentsId.nnw]: {
        description: <>Oświadczam, że w przypadku zgłoszenia szkody, wyrażam zgodę na pozyskiwanie przez TUZ TUW
          od:
          <ul>
            <li> placówek medycznych, lekarzy wszelkich informacji o stanie mojego zdrowia oraz wglądu do
              dokumentacji medycznej, w zakresie określonym przepisami prawa;
            </li>
            <li> Narodowego Funduszu Zdrowia wszelkich informacji o nazwach i adresach świadczeniodawców,
              którzy udzielali świadczeń opieki zdrowotnej w związku z wypadkiem objętym umową
              ubezpieczenia.
            </li>
          </ul>
        </>,
        required: true
      }
    });
  }

  public static getAdditionalConsents(): AdditionalConsents {
    return ({
      [AdditionalConsentsId.dataProcessingForMarketing]: {
        title: "Przekażemy ciekawe propozycje ubezpieczenia",
        description: <>Wyrażam zgodę na przetwarzanie przez TUZ Towarzystwo Ubezpieczeń Wzajemnych
          (dalej „TUZ”) moich danych osobowych dla celów marketingu produktów i usług TUZ, w tym
          w celach analitycznych i profilowania, również po zakończeniu umowy.</>,
        required: false
      },
      [AdditionalConsentsId.thirdPartyMarketing]: {
        title: "Wzbogacimy zakres Twojej ochrony ubezpieczeniowej",
        description: <>Wyrażam zgodę na udostępnianie przez TUZ Towarzystwo Ubezpieczeń Wzajemnych
          (dalej „TUZ”) moich danych osobowych w zakresie danych identyfikacyjnych i teleadresowych,
          podmiotom współpracującym z TUZ, do przetwarzania w celach marketingowych, w tym w celach
          analitycznych i profilowania.</>,
        required: false
      },
      [AdditionalConsentsId.electronicMeansMarketing]: {
        description: <>Wyrażam zgodę na przesyłanie informacji handlowych przez TUZ Towarzystwo Ubezpieczeń
          Wzajemnych, za pośrednictwem środków komunikacji elektronicznej, na wskazane adresy elektroniczne,
          np. numer telefonu, adres e-mail.</>,
        required: false
      },
      [AdditionalConsentsId.phoneCallMarketing]: {
        title: "Zostańmy razem! Przedstawimy m.in. ofertę wznowienia ubezpieczenia",
        description: <>Wyrażam zgodę na wykonywanie połączeń głosowych przez TUZ Towarzystwo Ubezpieczeń
          Wzajemnych, za pośrednictwem telekomunikacyjnych urządzeń końcowych i automatycznych systemów
          wywołujących, dla celów marketingu bezpośredniego.</>,
        required: false
      }
    });
  }
}
